<template>
  <LoadingWrapper :state="status">
    <SideBarLayoutContentHero>
      <template #hero>
        <div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
          <BaseCard class="flex flex-col p-6">
            <h1 class="text-headline-large leading-tight">
              {{ feedbackGoal.feedbackTopic.title }}
            </h1>
            <span class="text-body-medium text-on-surface-variant mb-2">
              {{ useDisplayDate(feedbackGoal.createdAt, { dayName: 'short' }) }}
            </span>
            <div class="mt-auto">
              <BaseButtonGroup size="M">
                <BaseComputedButtonGroup
                  class="flex w-full flex-row justify-end"
                  size="M"
                  menu-button-color="tonal"
                  :buttons="[
                    {
                      id: 'editMembers',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('survey.editMembers'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'group_add',
                      callback: openEditMembersDialog,
                    },
                    {
                      id: 'editNumbers',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('coaching.feedbackGoals.editNumbersAction'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'edit',
                      callback: openEditNumbersDialog,
                    },
                    {
                      id: 'delete',
                      flexibelConfigurations: [{ type: 'menu', order: 1 }],
                      label: $t('common.delete'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'delete',
                      destructive: true,
                      callback: openDeleteDialog,
                    },
                  ]"
                />
              </BaseButtonGroup>
            </div>
          </BaseCard>
          <BaseCard class="grid grid-cols-2 justify-start gap-4 p-6">
            <div class="mt-auto flex w-full flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant mr-2">people</BaseIcon>
              <div class="flex flex-col">
                <BaseMemberAvatars
                  class="bg-surface-container"
                  :members="feedbackGoal.coachees.map((c) => c.coachee)"
                />
                <span class="text-label-medium text-on-surface-variant">{{
                  $t('coaching.feedbackGoals.numberOfMembers', {
                    count: feedbackGoal.coachees.length,
                  })
                }}</span>
              </div>
            </div>
            <div class="relative flex h-36 w-full flex-col justify-end gap-2">
              <BaseProgressRing
                v-for="(perspective, index) in totalNumberOfAnswersPerPerspective"
                :key="perspective.perspectiveKey"
                :radius="getRadiusForIndex(index)"
                :thickness="18"
                :percentage="perspective.percentage"
                :icon="iconFor(perspective.perspectiveKey)"
                :color="perspective.color"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              />
              <span class="text-label-medium text-on-surface-variant">Total</span>
            </div>
          </BaseCard>
        </div>
      </template>
      <div class="overflow-x-auto">
        <table
          class="[&_tr>td]:bg-surface-container [&_tr>td]:rounded-small w-full border-separate border-spacing-0.5"
        >
          <thead>
            <tr class="">
              <th
                class="rounded-l-medium text-label-large text-on-surface px-4 py-3 text-left font-medium"
              ></th>
              <th
                v-for="perspectiveKey in allPerspectives"
                :key="perspectiveKey"
                class="text-label-large text-on-surface-variant last:rounded-r-medium px-4 py-3 text-left font-medium"
              >
                <div class="flex items-center gap-2">
                  <BaseIcon size="18">{{ iconFor(perspectiveKey) }}</BaseIcon>
                  {{ labelFor(perspectiveKey) }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="coachee in coacheesWithProgress"
              :key="coachee.id"
              class="bg-surface-container rounded-small last:rounded-b-large"
            >
              <td class="text-body-medium px-2 py-2">
                <div class="flex items-center gap-2">
                  <BaseMemberAvatar :member="coachee" />
                  {{ memberAbbreviatedName(coachee) }}
                </div>
              </td>
              <td
                v-for="perspectiveKey in allPerspectives"
                :key="perspectiveKey"
                class="text-body-medium px-4 py-3"
              >
                <div
                  v-if="progressFor(coachee, perspectiveKey)"
                  class="flex flex-row items-center gap-2"
                >
                  <BaseProgressRing
                    :radius="16"
                    :color="progressFor(coachee, perspectiveKey)!.color"
                    :percentage="progressFor(coachee, perspectiveKey)!.percentage"
                  />
                  <span class="text-label-medium text-on-surface-variant">
                    {{ progressFor(coachee, perspectiveKey)!.done }}/{{
                      progressFor(coachee, perspectiveKey)!.goal
                    }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </SideBarLayoutContentHero>
    <EditCoachFeedbackGoalMembersDialog
      v-if="feedbackGoal"
      :is-open="isEditMembersDialogOpen"
      :feedback-goal="feedbackGoal"
      @close-dialog="closeEditMembersDialog"
      @goal-updated="reload"
    />
    <EditCoachFeedbackGoalDialog
      v-if="feedbackGoal"
      :is-open="isEditNumbersDialogOpen"
      :feedback-goal="feedbackGoal"
      @close-dialog="closeEditNumbersDialog"
      @goal-updated="reload"
    />
    <DeleteCoachFeedbackGoalDialog
      :is-open="isDeleteDialogOpen"
      @close="closeDeleteDialog"
      @delete-goal="clickDeleteGoal"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useCoachFeedbackGoal } from '../composables/use-coach-feedback-goal'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import SideBarLayoutContentHero from '@/core/components/layouts/SideBarLayoutContentHero.vue'
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import { iconForPerspective } from '@/core/models/feedback-perspective'
  import { findPerspective, resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseProgressRing from '@/core/components/base/progress-ring/BaseProgressRing.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import BaseComputedButtonGroup from '@/core/components/base/button/BaseComputedButtonGroup.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import EditCoachFeedbackGoalMembersDialog from '../components/EditCoachFeedbackGoalMembersDialog.vue'
  import EditCoachFeedbackGoalDialog from '../components/EditCoachFeedbackGoalDialog.vue'
  import DeleteCoachFeedbackGoalDialog from '../components/DeleteCoachFeedbackGoalDialog.vue'
  import { useDisplayDate } from '@/core/composables/use-display-date'
  import { useFeedbackGoalProgress } from '@/modules/coachees/composables/use-coachee-feedback-goal-progress'
  import { memberAbbreviatedName } from '@/core/models/member/member-name'

  const route = useRoute()
  const { t } = useI18n()
  const feedbackGoalId = route.params.feedbackGoalId as string

  const { feedbackGoal, status, reload, deleteGoal } = useCoachFeedbackGoal(feedbackGoalId)

  const {
    isOpen: isEditMembersDialogOpen,
    openDialog: openEditMembersDialog,
    closeDialog: closeEditMembersDialog,
  } = useDialog()

  const {
    isOpen: isEditNumbersDialogOpen,
    openDialog: openEditNumbersDialog,
    closeDialog: closeEditNumbersDialog,
  } = useDialog()

  const {
    isOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog()

  const clickDeleteGoal = async () => {
    closeDeleteDialog()
    await deleteGoal()
  }

  const coachees = computed(() => feedbackGoal.value?.coachees ?? [])

  const topicPerspectives = computed(() => feedbackGoal.value?.feedbackTopic.perspectives ?? [])

  const iconFor = (key: string) =>
    iconForPerspective(findPerspective(key, topicPerspectives.value) ?? key)

  const labelFor = (key: string) =>
    resolvePerspectiveLabel(key, topicPerspectives.value, {
      kind: 'title',
      selfLabel: t('perspectives.self.default'),
    })

  const coacheesWithProgress = computed(() => {
    return coachees.value.map((coachee) => {
      const { perspectives } = useFeedbackGoalProgress(coachee.progress)
      return {
        ...coachee.coachee,
        perspectives: perspectives.value,
      }
    })
  })

  const allPerspectives = computed(() => {
    return [
      ...new Set(
        coacheesWithProgress.value.flatMap((coachee) =>
          coachee.perspectives.map((p) => p.perspectiveKey),
        ),
      ),
    ]
  })

  const totalNumberOfAnswersPerPerspective = computed(() => {
    return allPerspectives.value.map((perspectiveKey, index) => {
      const count = coacheesWithProgress.value.reduce((acc, coachee) => {
        return (
          acc + (coachee.perspectives.find((p) => p.perspectiveKey === perspectiveKey)?.done ?? 0)
        )
      }, 0)
      const target = coacheesWithProgress.value.reduce((acc, coachee) => {
        return (
          acc + (coachee.perspectives.find((p) => p.perspectiveKey === perspectiveKey)?.goal ?? 0)
        )
      }, 0)
      const color =
        coacheesWithProgress.value
          .flatMap((c) => c.perspectives)
          .find((p) => p.perspectiveKey === perspectiveKey)?.color ?? 'primary'
      const percentage = target > 0 ? (count / target) * 100 : 0
      return {
        perspectiveKey,
        count,
        target,
        percentage,
        color,
        index,
      }
    })
  })

  const getRadiusForIndex = (index: number) => 24 + index * 20

  const progressFor = (
    coachee: (typeof coacheesWithProgress.value)[number],
    perspectiveKey: string,
  ) => coachee.perspectives.find((p) => p.perspectiveKey === perspectiveKey)
</script>
